import React, { useState, useEffect, useMemo } from 'react';
import { useWorkbench } from '../WorkbenchContext';
import MarkdownViewer from './MarkdownViewer';
import WorkbenchChatInterface from './WorkbenchChatInterface';

const API_BASE_URL = '/api';

function RequirementsGathering() {
  const {
    requirementsOutputs,
    setRequirementsOutputs,
    currentRequirementsDocument,
    setCurrentRequirementsDocument,
    artifactTitle,
    setArtifactTitle,
    // Case context
    activeCase,
    cases,
    setActiveTab,
    // Reference navigation
    setSelectedReferenceCase
  } = useWorkbench();

  const [showSavedArtifacts, setShowSavedArtifacts] = useState(() => {
    const saved = localStorage.getItem('legal_workbenchLibraryVisible');
    return saved !== null ? saved === 'true' : true; // Default to showing library
  });
  const [selectedSavedArtifact, setSelectedSavedArtifact] = useState(null);
  const [isChatCollapsed, setIsChatCollapsed] = useState(() => {
    const saved = localStorage.getItem('legal_workbenchChatCollapsed');
    return saved !== null ? saved === 'true' : false; // Default open
  });
  const [isSaving, setIsSaving] = useState(false);

  // Check if the current document is saved in artifacts
  const isDocumentSaved = useMemo(() => {
    if (!currentRequirementsDocument || !artifactTitle) return true; // No document open
    return requirementsOutputs.some(a => a.title === artifactTitle);
  }, [currentRequirementsDocument, artifactTitle, requirementsOutputs]);

  // Save chat collapsed state
  useEffect(() => {
    localStorage.setItem('legal_workbenchChatCollapsed', isChatCollapsed.toString());
  }, [isChatCollapsed]);

  // Save library visibility state
  useEffect(() => {
    localStorage.setItem('legal_workbenchLibraryVisible', showSavedArtifacts.toString());
  }, [showSavedArtifacts]);

  // NOTE: Artifacts are now stored per-case in WorkbenchContext
  // No separate localStorage persistence needed here

  // Save document to artifacts and workspace
  const handleSaveDocument = async () => {
    if (!currentRequirementsDocument || !activeCase?.id) return;

    setIsSaving(true);
    try {
      // Generate workspace filename from title
      const filename = artifactTitle
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '') + '.md';

      // Save to workspace (backend)
      await fetch(`${API_BASE_URL}/workspace/case/${activeCase.id}/${filename}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          content: currentRequirementsDocument,
          description: `Saved from ${artifactTitle}`
        })
      });

      // Save to artifacts (frontend state, persisted via context)
      const savedArtifact = {
        id: Date.now().toString(),
        title: artifactTitle,
        content: currentRequirementsDocument,
        workspaceFile: filename,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        caseId: activeCase.id,
        caseName: activeCase.name
      };

      // Check if updating existing or adding new
      const existingIndex = requirementsOutputs.findIndex(a => a.title === artifactTitle);
      if (existingIndex >= 0) {
        setRequirementsOutputs(prev => {
          const updated = [...prev];
          updated[existingIndex] = { ...updated[existingIndex], ...savedArtifact };
          return updated;
        });
        setSelectedSavedArtifact(requirementsOutputs[existingIndex].id);
      } else {
        setRequirementsOutputs(prev => [savedArtifact, ...prev]);
        setSelectedSavedArtifact(savedArtifact.id);
      }

    } catch (error) {
      console.error('Error saving document:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle clicking on a case citation - navigate to Reference Library
  const handleCaseClick = (caseId) => {
    setSelectedReferenceCase(caseId);
    setActiveTab('references');
  };

  const handleLoadArtifact = (artifact) => {
    setCurrentRequirementsDocument(artifact.content);
    setArtifactTitle(artifact.title);
    setSelectedSavedArtifact(artifact.id);
    setShowSavedArtifacts(false);
  };

  const handleNewArtifact = () => {
    // Create a new document with placeholder content
    const placeholderContent = `# Untitled Document

*Use the AI Assistant to generate content, or edit this document directly.*

---

`;
    setCurrentRequirementsDocument(placeholderContent);
    setArtifactTitle('Untitled Document');
    setSelectedSavedArtifact(null);
    setShowSavedArtifacts(false);  // Close library to focus on new document
  };

  const handleDeleteArtifact = (artifactId) => {
    // Check if we're deleting the currently viewed artifact
    const artifactToDelete = requirementsOutputs.find(a => a.id === artifactId);
    const isDeletingCurrent = artifactToDelete &&
                               currentRequirementsDocument === artifactToDelete.content &&
                               artifactTitle === artifactToDelete.title;

    // Remove from state
    setRequirementsOutputs(prev => prev.filter(a => a.id !== artifactId));

    // Clear selection if this artifact was selected
    if (selectedSavedArtifact === artifactId) {
      setSelectedSavedArtifact(null);
    }

    // Clear current document if we're deleting it to prevent auto-save from restoring it
    if (isDeletingCurrent) {
      setCurrentRequirementsDocument(null);
      setArtifactTitle('Requirements Document');
    }
  };

  const handleDownloadArtifact = (artifact) => {
    const blob = new Blob([artifact.content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${artifact.title || 'requirements'}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // If no active case, show prompt to select one
  if (!activeCase) {
    return (
      <div className="requirements-gathering-container" style={{ display: 'flex', height: '100%' }}>
        <div className="requirements-gathering" style={{ flex: 1 }}>
          <div className="no-case-prompt">
            <div className="prompt-icon">&#9878;</div>
            <h2>No Active Case Selected</h2>
            <p>
              Document Analysis requires an active case. Please select or create a case first
              to organize your documents and analysis artifacts.
            </p>
            <button
              onClick={() => setActiveTab('my-cases')}
              className="btn-select-case"
            >
              Go to My Cases
            </button>
            {cases.length > 0 && (
              <p className="case-hint">
                You have {cases.length} case{cases.length !== 1 ? 's' : ''} available
              </p>
            )}
          </div>
        </div>

        {/* Chat Interface - Collapsed when no case */}
        <WorkbenchChatInterface
          isCollapsed={true}
          onToggleCollapse={() => setActiveTab('my-cases')}
        />

        <style>{`
          .no-case-prompt {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100%;
            text-align: center;
            padding: 3rem;
            background: rgba(15, 13, 38, 0.95);
          }

          .prompt-icon {
            font-size: 5rem;
            margin-bottom: 1.5rem;
          }

          .no-case-prompt h2 {
            color: #e0e9ff;
            font-size: 1.75rem;
            margin-bottom: 1rem;
          }

          .no-case-prompt p {
            color: rgba(224, 233, 255, 0.7);
            max-width: 500px;
            margin-bottom: 2rem;
            line-height: 1.6;
          }

          .btn-select-case {
            padding: 0.75rem 2rem;
            background: rgba(99, 102, 241, 0.8);
            border: 1px solid rgba(99, 102, 241, 0.9);
            border-radius: 8px;
            color: #fff;
            font-size: 1rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }

          .btn-select-case:hover {
            background: rgba(99, 102, 241, 1);
          }

          .case-hint {
            margin-top: 1.5rem;
            font-size: 0.875rem;
            color: rgba(224, 233, 255, 0.5);
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="requirements-gathering-container">
      {/* Main content area */}
      <div className="requirements-gathering">
        {/* Active Case Context Banner */}
        <div className="case-context-banner">
          <div className="case-info">
            <span className="case-icon">&#9878;</span>
            <span className="case-label">Working on:</span>
            <span className="case-name">{activeCase.name}</span>
          </div>
          <div className="case-stats">
            <span>{activeCase.linkedReferences.length} references</span>
            <span>&#x2022;</span>
            <span>{activeCase.documents.length} documents</span>
            <span>&#x2022;</span>
            <span>{activeCase.artifacts.length} artifacts</span>
          </div>
        </div>

        {/* Header with controls */}
        <div className="requirements-header">
          <div className="header-left">
            {currentRequirementsDocument && (
              <input
              type="text"
              value={artifactTitle}
              onChange={(e) => setArtifactTitle(e.target.value)}
              className="artifact-title-input"
              placeholder="Requirements Document Title"
            />
          )}
        </div>
        <div className="header-controls">
          {/* Save button - shows when document is unsaved */}
          {currentRequirementsDocument && !isDocumentSaved && (
            <button
              onClick={handleSaveDocument}
              disabled={isSaving}
              className="btn-save"
            >
              {isSaving ? '💾 Saving...' : '💾 Save Document'}
            </button>
          )}
          {/* Saved indicator */}
          {currentRequirementsDocument && isDocumentSaved && (
            <span className="saved-indicator">✓ Saved</span>
          )}
          {/* Library button */}
          <button
            onClick={() => setShowSavedArtifacts(!showSavedArtifacts)}
            className={`btn-library ${showSavedArtifacts ? 'active' : ''}`}
          >
            📚 Library ({requirementsOutputs.length})
          </button>
        </div>
      </div>

      <div className="requirements-body">
        {/* Simplified Library Sidebar - Only Saved Documents */}
        {showSavedArtifacts && (
          <div className="library-sidebar">
            <div className="sidebar-header">
              <div>
                <h3>📚 Document Library</h3>
                <button onClick={handleNewArtifact} className="btn-new-artifact">
                  + New Document
                </button>
              </div>
              <button onClick={() => setShowSavedArtifacts(false)} className="close-btn">✕</button>
            </div>

            {/* Working Document Indicator - shows when editing an artifact */}
            {(currentRequirementsDocument !== null) && (
              <div className={`working-document-banner ${!isDocumentSaved ? 'unsaved' : ''}`}>
                <div className="working-doc-header">
                  <span className="working-icon">📝</span>
                  <span className="working-label">
                    {isDocumentSaved ? 'Currently Editing' : '⚠️ Unsaved Document'}
                  </span>
                </div>
                <div className="working-doc-title">{artifactTitle}</div>
                <div className="working-doc-actions">
                  {!isDocumentSaved && (
                    <button
                      onClick={handleSaveDocument}
                      disabled={isSaving}
                      className="btn-save-doc"
                      title="Save to make available for AI editing"
                    >
                      {isSaving ? 'Saving...' : '💾 Save'}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setCurrentRequirementsDocument(null);
                      setArtifactTitle('Requirements Document');
                      setSelectedSavedArtifact(null);
                    }}
                    className="btn-close-doc"
                    title="Close document"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}

            {/* Your Documents - AI-generated artifacts */}
            <div className="library-section">
              <div className="section-header">
                <h4>📝 Your Documents ({requirementsOutputs.length})</h4>
              </div>

              <div className="section-content">
                {requirementsOutputs.length === 0 ? (
                  <div className="empty-state-small">
                    No saved documents yet. Create a new document or use the AI assistant to generate one.
                  </div>
                ) : (
                  requirementsOutputs.map(artifact => {
                    const isCurrentlyEditing = selectedSavedArtifact === artifact.id ||
                      (currentRequirementsDocument === artifact.content && artifactTitle === artifact.title);
                    return (
                      <div
                        key={artifact.id}
                        className={`artifact-item ${isCurrentlyEditing ? 'active editing' : ''}`}
                      >
                        <div onClick={() => handleLoadArtifact(artifact)} className="artifact-info-clickable">
                          <div className="artifact-item-title">
                            {artifact.title}
                            {isCurrentlyEditing && <span className="editing-indicator">●</span>}
                          </div>
                          <div className="artifact-item-date">
                            {new Date(artifact.createdAt).toLocaleDateString()}
                          </div>
                          {artifact.sourceFiles && artifact.sourceFiles.length > 0 && (
                            <div className="artifact-item-sources">
                              {artifact.sourceFiles.slice(0, 2).join(', ')}
                              {artifact.sourceFiles.length > 2 && ` +${artifact.sourceFiles.length - 2}`}
                            </div>
                          )}
                        </div>

                        {/* Artifact Actions - always visible, compact */}
                        <div className="artifact-actions-compact">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadArtifact(artifact);
                            }}
                            className="action-icon-btn"
                            title="Download markdown file"
                          >
                            💾
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`Delete "${artifact.title}"? This cannot be undone.`)) {
                                handleDeleteArtifact(artifact.id);
                              }
                            }}
                            className="action-icon-btn delete"
                            title="Delete artifact"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}

        {/* Main workspace */}
        <div className="workspace">
          {currentRequirementsDocument ? (
            <div className="artifact-viewer">
              <MarkdownViewer
                content={currentRequirementsDocument}
                title={artifactTitle}
                onCaseClick={handleCaseClick}
              />
            </div>
          ) : (
            <div className="workspace-empty">
              <div className="ready-to-work">
                <div className="ready-icon">📝</div>
                <h3>Ready to Create Documents</h3>
                <p>
                  {activeCase?.documents?.length > 0
                    ? `Your case "${activeCase.name}" has ${activeCase.documents.length} document(s) available as input.`
                    : 'Use the AI Assistant to draft legal documents based on your case materials.'}
                </p>
                <div className="workspace-actions">
                  <button
                    onClick={handleNewArtifact}
                    className="btn-create-doc"
                  >
                    + Create New Document
                  </button>
                  {requirementsOutputs.length > 0 && (
                    <button
                      onClick={() => setShowSavedArtifacts(true)}
                      className="btn-open-library"
                    >
                      📚 Open Library ({requirementsOutputs.length})
                    </button>
                  )}
                </div>
                <p className="assistant-hint">
                  Or use the AI Assistant panel to describe what you want to create
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .requirements-gathering {
          height: 100%;
          display: flex;
          flex-direction: column;
          background: rgba(15, 13, 38, 0.95);
        }

        .case-context-banner {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 1.5rem;
          background: rgba(99, 102, 241, 0.15);
          border-bottom: 1px solid rgba(99, 102, 241, 0.3);
        }

        .case-info {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .case-icon {
          font-size: 1.25rem;
        }

        .case-label {
          color: rgba(224, 233, 255, 0.7);
          font-size: 0.875rem;
        }

        .case-name {
          color: #e0e9ff;
          font-weight: 600;
        }

        .case-stats {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.75rem;
          color: rgba(224, 233, 255, 0.5);
        }

        .requirements-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 2rem 1rem 1.5rem;
          border-bottom: 1px solid rgba(99, 102, 241, 0.2);
          background: rgba(30, 27, 75, 0.6);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .artifact-title-input {
          background: rgba(99, 102, 241, 0.1);
          border: 1px solid rgba(99, 102, 241, 0.3);
          border-radius: 4px;
          padding: 0.5rem 1rem;
          color: #e0e9ff;
          font-size: 1.1rem;
          font-weight: 600;
          min-width: 300px;
        }

        .artifact-title-input:focus {
          outline: none;
          border-color: rgba(99, 102, 241, 0.6);
        }

        .header-controls {
          display: flex;
          gap: 0.75rem;
          align-items: center;
        }

        .btn-save, .btn-new, .btn-library, .btn-clear, .btn-upload {
          padding: 0.5rem 1rem;
          background: rgba(99, 102, 241, 0.2);
          border: 1px solid rgba(99, 102, 241, 0.4);
          border-radius: 4px;
          color: #e0e9ff;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-save {
          background: rgba(34, 197, 94, 0.3);
          border-color: rgba(34, 197, 94, 0.5);
        }

        .btn-save:hover {
          background: rgba(34, 197, 94, 0.4);
          border-color: rgba(34, 197, 94, 0.6);
        }

        .btn-save:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .saved-indicator {
          padding: 0.5rem 1rem;
          color: rgba(34, 197, 94, 0.9);
          font-size: 0.875rem;
          font-weight: 500;
        }

        .btn-save:hover, .btn-new:hover, .btn-library:hover, .btn-clear:hover, .btn-upload:hover {
          background: rgba(99, 102, 241, 0.3);
        }

        /* Library active state */
        .btn-library.active {
          background: rgba(99, 102, 241, 0.4);
          border-color: rgba(99, 102, 241, 0.6);
        }

        /* New Document button in Library sidebar */
        .btn-new-artifact {
          margin-top: 0.5rem;
          padding: 0.5rem 1rem;
          width: 100%;
          background: rgba(99, 102, 241, 0.3);
          border: 1px solid rgba(99, 102, 241, 0.5);
          border-radius: 4px;
          color: #e0e9ff;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.2s;
        }

        .btn-new-artifact:hover {
          background: rgba(99, 102, 241, 0.4);
        }

        /* Document title in header */
        .artifact-title-display {
          margin: 0;
          font-size: 1.2rem;
          color: #e0e9ff;
          font-weight: 600;
        }

        .requirements-body {
          flex: 1;
          display: flex;
          overflow: hidden;
        }

        .library-sidebar {
          width: 350px;
          background: rgba(30, 27, 75, 0.9);
          border-right: 1px solid rgba(99, 102, 241, 0.2);
          display: flex;
          flex-direction: column;
          overflow-y: auto;
        }

        .library-section {
          border-bottom: 1px solid rgba(99, 102, 241, 0.2);
          padding: 1rem;
        }

        .library-section:last-child {
          border-bottom: none;
        }

        /* Working Document Banner */
        .working-document-banner {
          margin: 1rem;
          padding: 1rem;
          background: rgba(34, 197, 94, 0.15);
          border: 1px solid rgba(34, 197, 94, 0.4);
          border-radius: 8px;
        }

        .working-document-banner.unsaved {
          background: rgba(251, 191, 36, 0.15);
          border-color: rgba(251, 191, 36, 0.5);
        }

        .working-document-banner.unsaved .working-label {
          color: rgba(251, 191, 36, 0.9);
        }

        .btn-save-doc {
          padding: 0.35rem 0.75rem;
          font-size: 0.75rem;
          background: rgba(34, 197, 94, 0.3);
          border: 1px solid rgba(34, 197, 94, 0.5);
          border-radius: 4px;
          color: #e0e9ff;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-save-doc:hover {
          background: rgba(34, 197, 94, 0.4);
        }

        .btn-save-doc:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .working-doc-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }

        .working-icon {
          font-size: 1rem;
        }

        .working-label {
          font-size: 0.75rem;
          color: rgba(34, 197, 94, 0.9);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .working-doc-title {
          font-size: 1rem;
          color: #e0e9ff;
          font-weight: 600;
          margin-bottom: 0.75rem;
        }

        .working-doc-actions {
          display: flex;
          gap: 0.5rem;
        }

        .btn-close-doc {
          padding: 0.35rem 0.75rem;
          font-size: 0.75rem;
          background: rgba(99, 102, 241, 0.2);
          border: 1px solid rgba(99, 102, 241, 0.3);
          border-radius: 4px;
          color: #e0e9ff;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-close-doc:hover {
          background: rgba(99, 102, 241, 0.3);
        }

        /* Editing indicator dot */
        .editing-indicator {
          margin-left: 0.5rem;
          color: rgba(34, 197, 94, 0.9);
          font-size: 0.75rem;
        }

        .artifact-item.editing {
          border-color: rgba(34, 197, 94, 0.4);
          background: rgba(34, 197, 94, 0.1);
        }

        .section-header {
          margin-bottom: 0.75rem;
        }

        .section-header h4 {
          margin: 0;
          font-size: 0.9rem;
          color: rgba(224, 233, 255, 0.9);
          font-weight: 600;
        }

        .section-content {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .files-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .files-controls {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }

        .files-controls button {
          flex: 1;
          padding: 0.25rem 0.5rem;
          font-size: 0.75rem;
          background: rgba(99, 102, 241, 0.2);
          border: 1px solid rgba(99, 102, 241, 0.3);
          border-radius: 4px;
          color: #e0e9ff;
          cursor: pointer;
          transition: all 0.2s;
        }

        .files-controls button:hover {
          background: rgba(99, 102, 241, 0.3);
        }

        .sidebar-header {
          padding: 1rem;
          border-bottom: 1px solid rgba(99, 102, 241, 0.2);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .close-btn {
          background: none;
          border: none;
          color: #e0e9ff;
          cursor: pointer;
          font-size: 1.25rem;
          padding: 0.25rem;
        }

        .artifact-actions {
          display: flex;
          gap: 0.5rem;
          margin-top: 0.5rem;
          padding-top: 0.5rem;
          border-top: 1px solid rgba(99, 102, 241, 0.2);
        }

        .action-btn-small {
          padding: 0.25rem 0.5rem;
          font-size: 0.75rem;
          background: rgba(99, 102, 241, 0.2);
          border: 1px solid rgba(99, 102, 241, 0.3);
          border-radius: 4px;
          color: #e0e9ff;
          cursor: pointer;
          transition: all 0.2s;
          flex: 1;
        }

        .action-btn-small:hover {
          background: rgba(99, 102, 241, 0.3);
        }

        .action-btn-small.delete {
          background: rgba(239, 68, 68, 0.2);
          border-color: rgba(239, 68, 68, 0.3);
        }

        .action-btn-small.delete:hover {
          background: rgba(239, 68, 68, 0.3);
        }

        .artifact-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem;
          margin-bottom: 0.5rem;
          background: rgba(99, 102, 241, 0.05);
          border: 1px solid rgba(99, 102, 241, 0.2);
          border-radius: 6px;
          transition: all 0.2s;
        }

        .artifact-item:hover {
          background: rgba(99, 102, 241, 0.1);
        }

        .artifact-item.active {
          background: rgba(99, 102, 241, 0.15);
          border-color: rgba(99, 102, 241, 0.4);
        }

        .artifact-info-clickable {
          flex: 1;
          cursor: pointer;
          min-width: 0;
        }

        .artifact-info-clickable:hover .artifact-item-title {
          color: rgba(139, 92, 246, 1);
        }

        .artifact-item-title {
          font-weight: 600;
          margin-bottom: 0.25rem;
          transition: color 0.2s;
        }

        .artifact-item-date {
          font-size: 0.75rem;
          color: rgba(224, 233, 255, 0.6);
        }

        .artifact-item-sources {
          font-size: 0.75rem;
          color: rgba(99, 102, 241, 0.8);
          margin-top: 0.25rem;
        }

        /* Case document items */
        .case-doc-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem;
          background: rgba(34, 197, 94, 0.05);
          border: 1px solid rgba(34, 197, 94, 0.2);
          border-radius: 6px;
          margin-bottom: 0.5rem;
        }

        .case-doc-item:hover {
          background: rgba(34, 197, 94, 0.1);
        }

        .case-doc-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        .case-doc-type {
          font-size: 0.7rem;
          color: rgba(34, 197, 94, 0.8);
          margin-top: 0.125rem;
        }

        .btn-load-doc {
          padding: 0.25rem 0.75rem;
          font-size: 0.75rem;
          background: rgba(34, 197, 94, 0.2);
          border: 1px solid rgba(34, 197, 94, 0.3);
          border-radius: 4px;
          color: #e0e9ff;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-load-doc:hover {
          background: rgba(34, 197, 94, 0.3);
        }

        /* Compact action buttons - always visible */
        .artifact-actions-compact {
          display: flex;
          gap: 0.25rem;
          margin-left: auto;
          flex-shrink: 0;
        }

        .action-icon-btn {
          background: rgba(99, 102, 241, 0.1);
          border: 1px solid rgba(99, 102, 241, 0.3);
          border-radius: 4px;
          color: #e0e9ff;
          cursor: pointer;
          padding: 0.35rem 0.5rem;
          font-size: 1rem;
          transition: all 0.2s;
          line-height: 1;
        }

        .action-icon-btn:hover {
          background: rgba(99, 102, 241, 0.3);
          border-color: rgba(99, 102, 241, 0.5);
        }

        .action-icon-btn.delete:hover {
          background: rgba(239, 68, 68, 0.2);
          border-color: rgba(239, 68, 68, 0.5);
          color: #fca5a5;
        }

        .delete-btn {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 1rem;
          padding: 0.25rem;
          opacity: 0.6;
          transition: opacity 0.2s;
        }

        .delete-btn:hover {
          opacity: 1;
        }

        .workspace {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .artifact-viewer {
          flex: 1;
          overflow: hidden;
        }

        .workspace-empty {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
        }

        /* Ready to work - main empty state */
        .ready-to-work {
          max-width: 500px;
          width: 100%;
          text-align: center;
        }

        .ready-to-work h3 {
          margin-bottom: 0.75rem;
          color: #e0e9ff;
        }

        .ready-to-work > p {
          color: rgba(224, 233, 255, 0.7);
          margin-bottom: 1.5rem;
          line-height: 1.5;
        }

        .workspace-actions {
          display: flex;
          gap: 1rem;
          justify-content: center;
          margin-bottom: 1.5rem;
        }

        .btn-create-doc {
          padding: 0.75rem 1.5rem;
          background: rgba(34, 197, 94, 0.8);
          border: 1px solid rgba(34, 197, 94, 0.9);
          border-radius: 8px;
          color: #fff;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-create-doc:hover {
          background: rgba(34, 197, 94, 1);
        }

        .btn-open-library {
          padding: 0.75rem 1.5rem;
          background: rgba(99, 102, 241, 0.2);
          border: 1px solid rgba(99, 102, 241, 0.3);
          border-radius: 8px;
          color: #e0e9ff;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-open-library:hover {
          background: rgba(99, 102, 241, 0.3);
        }

        .assistant-hint {
          font-size: 0.875rem;
          color: rgba(224, 233, 255, 0.5);
        }

        .ready-icon {
          font-size: 4rem;
          text-align: center;
          margin-bottom: 1rem;
        }

        .files-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .files-header h4 {
          margin: 0;
        }

        .file-controls {
          display: flex;
          gap: 0.5rem;
        }

        .btn-select {
          padding: 0.25rem 0.5rem;
          font-size: 0.75rem;
          background: rgba(99, 102, 241, 0.2);
          border: 1px solid rgba(99, 102, 241, 0.3);
          border-radius: 4px;
          color: #e0e9ff;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-select:hover {
          background: rgba(99, 102, 241, 0.3);
        }

        .file-item {
          display: flex;
          align-items: center;
          padding: 0.75rem;
          margin-bottom: 0.5rem;
          background: rgba(99, 102, 241, 0.1);
          border-radius: 4px;
        }

        .file-checkbox {
          margin-right: 0.75rem;
          cursor: pointer;
          width: 16px;
          height: 16px;
        }

        .file-icon {
          margin-right: 0.75rem;
          font-size: 1.5rem;
        }

        .file-name {
          flex: 1;
        }

        .file-size {
          margin-right: 1rem;
          color: rgba(224, 233, 255, 0.6);
          font-size: 0.875rem;
        }

        .remove-file-btn {
          background: none;
          border: none;
          color: rgba(239, 68, 68, 0.8);
          cursor: pointer;
          font-size: 1.25rem;
          padding: 0.25rem;
          opacity: 0.6;
          transition: opacity 0.2s;
        }

        .remove-file-btn:hover {
          opacity: 1;
        }

        .add-more {
          background: rgba(30, 27, 75, 0.4);
          border-radius: 8px;
          padding: 1rem;
        }

        .add-more h4 {
          margin-bottom: 1rem;
          text-align: center;
        }

        .empty-state-small {
          padding: 2rem;
          text-align: center;
          color: rgba(224, 233, 255, 0.5);
        }

        .requirements-gathering-container {
          display: flex;
          height: 100%;
          overflow: hidden;
        }

        .requirements-gathering-container .requirements-gathering {
          flex: 1;
          min-width: 0;
          overflow: hidden;
        }
      `}</style>
      </div>

      {/* Chat Interface - Right Side */}
      <WorkbenchChatInterface
        isCollapsed={isChatCollapsed}
        onToggleCollapse={() => setIsChatCollapsed(!isChatCollapsed)}
      />
    </div>
  );
}

export default RequirementsGathering;
